import { Location, NgClass } from '@angular/common';
import {
  afterRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { select } from 'd3-selection';
import {
  D3ZoomEvent,
  zoom,
  ZoomBehavior,
  zoomIdentity,
  ZoomTransform,
  zoomTransform,
} from 'd3-zoom';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { PermissionService, triggerIfSlow } from '@ipg/ngx-frontend-toolset';
import { AiAgentChatComponent } from '../../components/ai-agent-chat/ai-agent-chat.component';
import { ChatInteractionPreview } from '../../components/ai-agent-chat/ai-agent-chat.models';
import { DialogService } from '../../components/dialog/dialog.service';
import { HeaderComponent } from '../../components/header/header.component';
import { InteractionEditorStateService } from '../../components/interaction-editor/services/interaction-editor-state.service';
import { GlobalLoadersService } from '../../global-loaders.service';
import { AiAgentChatLayoutService } from '../../services/ai-agent-chat-layout.service';
import {
  GotoInteractionAction,
  Interaction,
  InteractionAction,
  InteractionAnswer,
  InteractionQuestion,
  InteractionStatus,
} from '../../models/interactions.model';
import { MapFromPipe } from '../../pipes/map-from.pipe';
import { InteractionService } from '../../services/interaction.service';
import { VarService } from '../../services/var.service';
import { QuestionTreeNode } from '../interaction-editor/interaction-editor.component';
import { FlowAnswerNodeComponent } from './components/flow-answer-node/flow-answer-node.component';
import { FlowQuestionNodeComponent } from './components/flow-question-node/flow-question-node.component';
import { FlowSubflowHeaderComponent } from './components/flow-subflow-header/flow-subflow-header.component';
import { FlowEdge, FlowLayout, FlowSubflowHeader } from './interaction-flow.model';

import 'd3-transition';

function previewToFlowData(preview: ChatInteractionPreview): {
  questions: InteractionQuestion[];
  answers: InteractionAnswer[];
  actions: InteractionAction[];
} {
  const raw = preview.rawJson;
  if (raw && Array.isArray(raw['questions'])) {
    const rawQuestions = raw['questions'] as Array<Record<string, unknown>>;
    const rawAnswers = (
      Array.isArray(raw['answers']) ? raw['answers'] : []
    ) as Array<Record<string, unknown>>;
    const rawActions = (
      Array.isArray(raw['actions']) ? raw['actions'] : []
    ) as Array<Record<string, unknown>>;

    const questions: InteractionQuestion[] = rawQuestions.map((q, idx) => ({
      _id: String(q['id'] ?? q['_id'] ?? `q${idx + 1}`),
      value: (q['value'] ?? q['question'] ?? `Vraag ${idx + 1}`) as string,
      index: typeof q['index'] === 'number' ? q['index'] : idx,
    }));

    const answers: InteractionAnswer[] = rawAnswers.map((a, idx) => ({
      _id: String(a['id'] ?? a['_id'] ?? `a${idx + 1}`),
      type: 'text',
      questionId: String(a['question_id'] ?? a['questionId'] ?? ''),
      value: (a['value'] ?? '') as string,
      index: typeof a['index'] === 'number' ? a['index'] : idx,
    }));

    const actions: InteractionAction[] = rawActions.map(
      (act, idx) =>
        ({
          _id: String(act['id'] ?? act['_id'] ?? `act${idx + 1}`),
          questionId: String(act['question_id'] ?? act['questionId'] ?? ''),
          answerId: String(act['answer_id'] ?? act['answerId'] ?? ''),
          type: (act['type'] ?? 'speak') as any,
          value: act['value'] ?? null,
          index: typeof act['index'] === 'number' ? act['index'] : idx,
        }) as unknown as InteractionAction
    );

    return { questions, answers, actions };
  }

  const questions: InteractionQuestion[] = [];
  const answers: InteractionAnswer[] = [];
  const actions: InteractionAction[] = [];

  (preview.questions || []).forEach((q, qIdx) => {
    const qId = `q${qIdx + 1}`;
    questions.push({
      _id: qId,
      value: q.question,
      index: qIdx,
    });

    (q.answers || []).forEach((ans, aIdx) => {
      const aId = `a${qIdx + 1}_${aIdx + 1}`;
      answers.push({
        _id: aId,
        type: 'text',
        questionId: qId,
        value: ans,
        index: aIdx,
      });
    });
  });

  return { questions, answers, actions };
}

interface ComputedEdge {
  id: string;
  path: string;
  stroke: string;
  dashArray: string;
  markerEnd: string;
}

@Component({
  selector: 'app-interaction-flow',
  imports: [
    NgClass,
    MatButtonModule,
    MatIconModule,
    MatTooltip,
    MatDrawerContainer,
    MatDrawer,
    MatDrawerContent,
    MapFromPipe,
    FlowQuestionNodeComponent,
    FlowAnswerNodeComponent,
    FlowSubflowHeaderComponent,
    AiAgentChatComponent,
  ],
  templateUrl: './interaction-flow.component.html',
  styleUrl: './interaction-flow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InteractionFlowComponent implements AfterViewInit, OnDestroy {
  private static readonly LOADER_CALLER_ID = 'interaction_flow_loader_caller';

  // Layout constants — tunable per design iteration
  private static readonly COLUMN_0_X = 60;
  private static readonly COLUMN_1_X = 500;
  private static readonly NODE_WIDTH = 360;
  /** Placeholder height used before ResizeObserver measurements (Phase 4). */
  private static readonly NODE_HEIGHT_ESTIMATE = 80;
  private static readonly NODE_GAP_Y = 40;
  private static readonly GROUP_GAP_Y = 60;
  private static readonly SUBFLOW_GAP_X = 160;

  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _location = inject(Location);
  private readonly _dialog = inject(DialogService);
  private readonly _interactionService = inject(InteractionService);
  private readonly _editorState = inject(InteractionEditorStateService);
  private readonly _globalLoaders = inject(GlobalLoadersService);
  private readonly _varService = inject(VarService);
  private readonly _permissionService = inject(PermissionService);

  private readonly _canvasOuter =
    viewChild.required<ElementRef<HTMLDivElement>>('canvasOuter');

  // Stored for programmatic zoom access in later phases (zoomIn, zoomOut, fitToScreen)
  private _zoomBehavior!: ZoomBehavior<HTMLDivElement, unknown>;
  private readonly _transform = signal<ZoomTransform>(zoomIdentity);
  private readonly _nodeHeights = signal<Map<string, number>>(new Map());
  private _resizeObserver?: ResizeObserver;
  private readonly _linkedInteractionsCancel$ = new Subject<void>();
  private readonly _linkedInteractions = signal<Map<string, Interaction>>(new Map());
  private readonly _reloadTrigger$ = new Subject<void>();

  protected readonly chatLayout = inject(AiAgentChatLayoutService);
  protected readonly isLoading = signal(false);
  protected readonly isNewFlow = computed(
    () => this._route.snapshot.paramMap.get('interactionId') === 'new'
  );
  protected readonly isChatOpen = signal<boolean>(
    this._route.snapshot.paramMap.get('interactionId') === 'new' &&
      this._route.snapshot.queryParamMap.get('ai') === 'true'
  );

  protected readonly aiPreview = signal<ChatInteractionPreview | null>(null);

  private readonly _interactionId$ = this._route.paramMap.pipe(
    map((params) => params.get('interactionId')),
    filter((value): value is string => value != null && value !== 'new')
  );

  private readonly _interaction$ = combineLatest([
    this._interactionId$,
    this._reloadTrigger$.pipe(startWith(undefined)),
  ]).pipe(
    map(([interactionId]) => interactionId),
    takeUntilDestroyed(),
    tap(() => this.isLoading.set(true)),
    switchMap((interactionId) =>
      this._interactionService.getById(interactionId).pipe(
        triggerIfSlow(() =>
          this._globalLoaders.setActive(
            InteractionFlowComponent.LOADER_CALLER_ID,
            HeaderComponent.LOADER_ID
          )
        ),
        catchError(() => {
          this._dialog.alert({
            title: 'Foutmelding',
            message:
              'Er is iets misgegaan bij het ophalen van de interactie. Probeer het later nog eens',
            confirmText: 'OK',
          });

          return EMPTY;
        }),
        tap((result) => {
          this._editorState.setState(result);
        }),
        switchMap((result) =>
          this._loadLinkedInteractions(result.actions).pipe(
            finalize(() => {
              this._globalLoaders.setInactive(
                InteractionFlowComponent.LOADER_CALLER_ID,
                HeaderComponent.LOADER_ID
              );
              this.isLoading.set(false);
            })
          )
        )
      )
    )
  );

  protected readonly interaction = this._editorState.interaction;
  protected readonly clientId = computed(
    () =>
      this.interaction()?.clientId ??
      this._route.snapshot.queryParamMap.get('clientId') ??
      null
  );

  protected readonly currentRoutePath = toSignal(
    this._route.url.pipe(
      map((urlSegments) => urlSegments.at(0)),
      filter(Boolean),
      map((segment) => segment.path as 'interactions' | 'templates')
    )
  );

  protected readonly statusMapping: Record<InteractionStatus, string> = {
    published: 'Gepubliceerd',
    draft: 'Concept',
  };

  protected readonly transformStyle = computed(() => {
    const t = this._transform();

    return `translate(${t.x}px, ${t.y}px) scale(${t.k})`;
  });

  protected readonly zoomPercent = computed(() =>
    Math.round(this._transform().k * 100)
  );

  protected readonly displayInteraction = computed<Partial<Interaction> | null>(() => {
    const real = this.interaction();
    if (real && !this.isNewFlow()) {
      return real;
    }
    const preview = this.aiPreview();
    if (preview) {
      const flowData = previewToFlowData(preview);
      return {
        _id: 'preview',
        label: preview.title || 'Nieuwe interactie',
        status: 'draft' as InteractionStatus,
        version: 1,
        clientId: this.clientId(),
        ...flowData,
      };
    }
    return {
      _id: 'new',
      label:
        this.currentRoutePath() === 'templates'
          ? 'Nieuwe template'
          : 'Nieuwe interactie',
      status: 'draft' as InteractionStatus,
      version: 1,
      clientId: this.clientId(),
      questions: [],
      answers: [],
      actions: [],
    };
  });

  protected readonly dataSourceTree = computed(() => {
    const inter = this.displayInteraction();
    const questions = inter?.questions ?? [];
    const answers = inter?.answers ?? [];
    const actions = inter?.actions ?? [];

    return this._buildTree(
      questions,
      answers,
      actions,
      this._linkedInteractions()
    );
  });

  protected readonly layout = computed(() =>
    this._computeLayout({
      tree: this.dataSourceTree(),
      nodeHeights: this._nodeHeights(),
      linkedInteractions: this._linkedInteractions(),
      mainInteractionLabel: this.displayInteraction()?.label,
      mainInteractionVersion: this.displayInteraction()?.version,
      mainInteractionStatus: this.displayInteraction()?.status,
      mainInteractionKind:
        this.currentRoutePath() === 'templates' ? 'template' : 'interaction',
    })
  );

  /**
   * Maps question ID → question label for every question in the main interaction
   * and all linked interactions. Passed to answer nodes so they can resolve
   * goto-question targets into human-readable labels.
   */
  protected readonly gotoQuestionLabelMap = computed(() => {
    const labelMap = new Map<string, string>();
    for (const question of this.displayInteraction()?.questions ?? []) {
      const qId = String((question as any)._id || (question as any).id || '');
      if (qId) {
        labelMap.set(qId, question.value as string);
      }
    }
    for (const [, linkedInteraction] of this._linkedInteractions()) {
      for (const question of linkedInteraction.questions || []) {
        const qId = String((question as any)._id || (question as any).id || '');
        if (qId) {
          labelMap.set(qId, question.value as string);
        }
      }
    }
    return labelMap;
  });

  /**
   * Maps interaction ID → first question node ID for each linked sub-flow.
   * Passed to answer nodes so they can resolve goto-interaction targets.
   */
  protected readonly gotoInteractionTargetMap = computed(() => {
    const map = new Map<string, string>();
    const questionNodes = this.layout().questionNodes;
    const questionNodeIds = new Set(questionNodes.map((n) => n.id));

    for (const [interactionId, interaction] of this._linkedInteractions()) {
      const sorted = [...(interaction.questions || [])].sort(
        (a, b) => (a.index ?? 0) - (b.index ?? 0)
      );
      const firstQ = sorted[0] as any;
      const firstId = firstQ ? String(firstQ._id || firstQ.id || '') : '';
      if (firstId && questionNodeIds.has(firstId)) {
        map.set(interactionId, firstId);
        const interId = String(
          (interaction as any)._id || (interaction as any).id || ''
        );
        if (interId) map.set(interId, firstId);
        const corrId = String(
          (interaction as any).correlationId ||
            (interaction as any).correlation_id ||
            ''
        );
        if (corrId) map.set(corrId, firstId);
      }
    }

    return map;
  });

  protected readonly computedEdges = computed((): ComputedEdge[] => {
    const layout = this.layout();
    const nodeHeights = this._nodeHeights();
    const { NODE_WIDTH, NODE_HEIGHT_ESTIMATE } = InteractionFlowComponent;

    const questionMap = new Map(layout.questionNodes.map((n) => [n.id, n]));
    const answerMap = new Map(layout.answerNodes.map((n) => [n.id, n]));
    const h = (id: string) => nodeHeights.get(id) ?? NODE_HEIGHT_ESTIMATE;

    return layout.edges.map((edge): ComputedEdge => {
      switch (edge.type) {
        case 'q-to-a': {
          const from = questionMap.get(edge.fromId);
          const to = answerMap.get(edge.toId);
          if (!from || !to) {
            return {
              id: edge.id,
              path: '',
              stroke: '#9ca3af',
              dashArray: '',
              markerEnd: 'url(#arrowhead)',
            };
          }
          const x1 = from.x + NODE_WIDTH;
          const y1 = from.y + h(edge.fromId) / 2;
          // End 11 px before the node so the arrowhead tip (markerWidth=11, refX=0)
          // lands exactly at the node left edge.
          const x2 = to.x - 11;
          const y2 = to.y + h(edge.toId) / 2;
          const r = 6;
          const midX = x1 + 40;
          let path: string;
          if (Math.abs(y2 - y1) < 1) {
            // Straight horizontal — no bends needed
            path = `M ${x1},${y1} H ${x2}`;
          } else if (y2 > y1) {
            // Going down: turn right→down, then down→right
            path =
              `M ${x1},${y1} ` +
              `H ${midX - r} ` +
              `a ${r},${r} 0 0 1 ${r},${r} ` +
              `V ${y2 - r} ` +
              `a ${r},${r} 0 0 0 ${r},${r} ` +
              `H ${x2}`;
          } else {
            // Going up: turn right→up, then up→right
            path =
              `M ${x1},${y1} ` +
              `H ${midX - r} ` +
              `a ${r},${r} 0 0 0 ${r},${-r} ` +
              `V ${y2 + r} ` +
              `a ${r},${r} 0 0 1 ${r},${-r} ` +
              `H ${x2}`;
          }
          return {
            id: edge.id,
            path,
            stroke: '#9ca3af',
            dashArray: '',
            markerEnd: 'url(#arrowhead)',
          };
        }

        case 'goto-question': {
          const from = answerMap.get(edge.fromId);
          const to = questionMap.get(edge.toId);
          if (!from || !to) {
            return {
              id: edge.id,
              path: '',
              stroke: '#0d9488',
              dashArray: '6 4',
              markerEnd: 'url(#arrowhead-teal)',
            };
          }
          const x1 = from.x + NODE_WIDTH;
          const y1 = from.y + h(edge.fromId) / 2;
          // The Bezier swings far left and arrives at the question node's left edge
          // from the left side (rightward tangent). Stop 11 px short so the
          // arrowhead base is outside the node and the tip lands at the node edge.
          const x2 = to.x - 11;
          const y2 = to.y + h(edge.toId) / 2;
          // Use an asymmetric S-curve: the second control point is pulled farther
          // from the endpoint so the curve naturally levels out at y2 well before
          // the arrowhead, keeping dashes centre-aligned without a separate H segment.
          const cpOffset1 = 120;
          const cpOffset2 = 300;
          return {
            id: edge.id,
            path:
              `M ${x1},${y1} C ${x1 + cpOffset1},${y1}` +
              ` ${x2 - cpOffset2},${y2} ${x2},${y2}`,
            stroke: '#0d9488',
            dashArray: '6 4',
            markerEnd: 'url(#arrowhead-teal)',
          };
        }

        case 'goto-interaction': {
          const from = answerMap.get(edge.fromId);
          const to = questionMap.get(edge.toId);
          if (!from || !to) {
            return {
              id: edge.id,
              path: '',
              stroke: '#ea580c',
              dashArray: '6 4',
              markerEnd: 'url(#arrowhead-orange)',
            };
          }
          const x1 = from.x + NODE_WIDTH;
          const y1 = from.y + h(edge.fromId) / 2;
          // goto-interaction goes LEFT→RIGHT: end 11 px before the node so the
          // arrowhead tip (markerWidth=11, refX=0) lands at the node left edge.
          const x2 = to.x - 11;
          const y2 = to.y + h(edge.toId) / 2;
          // Use an asymmetric S-curve: the second control point is pulled farther
          // from the endpoint so the curve naturally levels out at y2 well before
          // the arrowhead, keeping dashes centre-aligned without a separate H segment.
          const cpOffset1 = 120;
          const cpOffset2 = 300;
          return {
            id: edge.id,
            path:
              `M ${x1},${y1} C ${x1 + cpOffset1},${y1}` +
              ` ${x2 - cpOffset2},${y2} ${x2},${y2}`,
            stroke: '#ea580c',
            dashArray: '6 4',
            markerEnd: 'url(#arrowhead-orange)',
          };
        }
      }
    });
  });

  constructor() {
    this._interaction$.subscribe();

    // Variables only highlight placeholders in the texts, and their endpoints
    // need `var:read`, which `interaction:read` does not imply: without it the
    // flow renders with plain text. Waiting on the signal, because permissions
    // arrive with the user fetch, which `authGuard` does not await.
    toObservable(
      computed(() => this._permissionService.hasPermission('var:read'))
    )
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() =>
          forkJoin([
            this._varService.getAll(),
            this._varService.getPendingDeletionVars(),
          ])
        ),
        takeUntilDestroyed()
      )
      .subscribe();

    this._resizeObserver = new ResizeObserver((entries) => {
      const updated = new Map(this._nodeHeights());
      let changed = false;
      for (const entry of entries) {
        const id = (entry.target as HTMLElement).dataset['nodeId'];
        if (id) {
          const newHeight = entry.contentRect.height;
          if (updated.get(id) !== newHeight) {
            updated.set(id, newHeight);
            changed = true;
          }
        }
      }
      if (changed) this._nodeHeights.set(updated);
    });

    afterRender(() => {
      const canvas = this._canvasOuter().nativeElement;
      const nodes = canvas.querySelectorAll<HTMLElement>('[data-node-id]');
      for (const el of nodes) {
        this._resizeObserver?.observe(el);
      }
    });
  }

  ngAfterViewInit(): void {
    this._zoomBehavior = zoom<HTMLDivElement, unknown>()
      .scaleExtent([0.1, 3.0])
      .filter(() => !this.isLoading())
      .on('zoom', (event: D3ZoomEvent<HTMLDivElement, unknown>) => {
        this._transform.set(event.transform);
      });

    select<HTMLDivElement, unknown>(this._canvasOuter().nativeElement).call(
      this._zoomBehavior
    );
  }

  ngOnDestroy(): void {
    this._linkedInteractionsCancel$.next();
    this._linkedInteractionsCancel$.complete();
    this._editorState.clearState();
    this._resizeObserver?.disconnect();
  }

  private _loadLinkedInteractions(actions: InteractionAction[]): Observable<void> {
    this._linkedInteractionsCancel$.next();

    const initialIds = [
      ...new Set(
        actions
          .filter((a): a is GotoInteractionAction => a.type === 'goto-interaction')
          .map((a) => {
            if (typeof a.value === 'string') {
              return a.value;
            }
            if (typeof a.value === 'object' && a.value !== null) {
              return (
                (a.value as any)._id ||
                (a.value as any).id ||
                (a.value as any).correlationId
              );
            }
            return undefined;
          })
          .filter((id): id is string => !!id)
      ),
    ];

    if (initialIds.length === 0) {
      this._linkedInteractions.set(new Map());
      return of(undefined);
    }

    // BFS: load interactions level by level until no new references are found
    const accumulated = new Map<string, Interaction>();
    const loadLevel = (ids: string[]): Observable<void> => {
      const unloaded = ids.filter((id) => !accumulated.has(id));
      if (unloaded.length === 0) {
        this._linkedInteractions.set(new Map(accumulated));
        return of(undefined);
      }

      return forkJoin(
        Object.fromEntries(
          unloaded.map((id) => [
            id,
            this._interactionService
              .getById(id)
              .pipe(catchError(() => of(null))),
          ])
        )
      ).pipe(
        takeUntil(this._linkedInteractionsCancel$),
        switchMap((results) => {
          for (const [id, interaction] of Object.entries(results)) {
            if (interaction) {
              accumulated.set(id, interaction);
              const interId = String(
                (interaction as any)._id || (interaction as any).id || ''
              );
              if (interId) accumulated.set(interId, interaction);
              const corrId = String(
                (interaction as any).correlationId ||
                  (interaction as any).correlation_id ||
                  ''
              );
              if (corrId) accumulated.set(corrId, interaction);
            }
          }

          // Collect next level of referenced interaction IDs
          const nextIds = [
            ...new Set(
              Object.values(results)
                .filter((interaction): interaction is Interaction => !!interaction)
                .flatMap((interaction) =>
                  interaction.actions
                    .filter(
                      (a): a is GotoInteractionAction =>
                        a.type === 'goto-interaction'
                    )
                    .map((a) => {
                      if (typeof a.value === 'string') {
                        return a.value;
                      }
                      if (typeof a.value === 'object' && a.value !== null) {
                        return (
                          (a.value as any)._id ||
                          (a.value as any).id ||
                          (a.value as any).correlationId
                        );
                      }
                      return undefined;
                    })
                    .filter((id): id is string => !!id)
                )
            ),
          ];

          return loadLevel(nextIds);
        })
      );
    };

    return loadLevel(initialIds);
  }

  protected zoomIn(): void {
    const el = this._canvasOuter().nativeElement;
    const k = Math.min(Math.round((zoomTransform(el).k + 0.2) * 10) / 10, 3.0);
    select<HTMLDivElement, unknown>(el).call(this._zoomBehavior.scaleTo, k);
  }

  protected zoomOut(): void {
    const el = this._canvasOuter().nativeElement;
    const k = Math.max(Math.round((zoomTransform(el).k - 0.2) * 10) / 10, 0.1);
    select<HTMLDivElement, unknown>(el).call(this._zoomBehavior.scaleTo, k);
  }

  protected resetZoom(): void {
    select<HTMLDivElement, unknown>(this._canvasOuter().nativeElement).call(
      this._zoomBehavior.scaleTo,
      1
    );
  }

  protected panToNode(nodeId: string): void {
    const layout = this.layout();
    const node = layout.questionNodes.find((n) => n.id === nodeId);
    if (!node) return;

    const { NODE_HEIGHT_ESTIMATE, NODE_WIDTH } = InteractionFlowComponent;
    const container = this._canvasOuter().nativeElement;
    const { clientWidth, clientHeight } = container;
    const nodeHeight = this._nodeHeights().get(nodeId) ?? NODE_HEIGHT_ESTIMATE;
    const k = this._transform().k;

    const x = clientWidth / 2 - (node.x + NODE_WIDTH / 2) * k;
    const y = clientHeight / 2 - (node.y + nodeHeight / 2) * k;

    select<HTMLDivElement, unknown>(this._canvasOuter().nativeElement)
      .transition()
      .duration(1000)
      .call(this._zoomBehavior.transform, zoomIdentity.translate(x, y).scale(k));
  }

  protected fitToScreen(): void {
    const { canvasWidth, canvasHeight } = this.layout();
    const container = this._canvasOuter().nativeElement;
    const { clientWidth, clientHeight } = container;
    const padding = 40;
    const scaleX = (clientWidth - padding * 2) / canvasWidth;
    const scaleY = (clientHeight - padding * 2) / canvasHeight;
    const k = Math.min(Math.max(Math.min(scaleX, scaleY), 0.1), 3.0);
    const x = (clientWidth - canvasWidth * k) / 2;
    const y = (clientHeight - canvasHeight * k) / 2;
    select<HTMLDivElement, unknown>(this._canvasOuter().nativeElement).call(
      this._zoomBehavior.transform,
      zoomIdentity.translate(x, y).scale(k)
    );
  }

  protected toggleChat(): void {
    if (this.isNewFlow()) {
      this.isChatOpen.update((open) => !open);
    }
  }

  protected closeChat(): void {
    this.isChatOpen.set(false);
  }

  protected handleAiPreviewChanged(
    preview: ChatInteractionPreview | null
  ): void {
    this.aiPreview.set(preview);
    if (preview) {
      const flowData = previewToFlowData(preview);
      this._loadLinkedInteractions(flowData.actions).subscribe();
    } else {
      this._linkedInteractions.set(new Map());
    }
  }

  protected goBack(): void {
    const path = this.currentRoutePath();
    if (path === 'templates') {
      this._router.navigate(['templates']);
    } else {
      const clientId = this.clientId();
      if (clientId) {
        this._router.navigate(['clients', clientId, 'interactions']);
      } else {
        this._router.navigate(['interactions']);
      }
    }
  }

  protected navigateToEditor(): void {
    if (this.isNewFlow()) {
      return;
    }
    const path = this.currentRoutePath();
    const id = this.interaction()?._id;
    if (path && id) {
      this._router.navigate([path, id]);
    }
  }

  protected handleInteractionCreated(newId: string): void {
    this.goBack();
  }

  private _buildTree(
    questions: InteractionQuestion[],
    answers: InteractionAnswer[],
    actions: InteractionAction[],
    linkedInteractions?: Map<string, Interaction>
  ): QuestionTreeNode[] {
    return (questions || [])
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
      .map((q) => {
        const qId = String((q as any)._id || (q as any).id || '');
        const qChildren = (answers || [])
          .filter((a) => {
            const aQid = String(
              (a as any).questionId || (a as any).question_id || ''
            );
            return aQid === qId;
          })
          .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
          .map((a) => {
            const aId = String((a as any)._id || (a as any).id || '');
            const aChildren = (actions || [])
              .filter((act) => {
                const actAid = String(
                  (act as any).answerId || (act as any).answer_id || ''
                );
                return actAid === aId;
              })
              .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
              .map((act) => {
                const actionObj = {
                  ...(act as any),
                  _id: String((act as any)._id || (act as any).id || ''),
                  id: String((act as any)._id || (act as any).id || ''),
                };
                if (
                  actionObj.type === 'goto-interaction' &&
                  typeof actionObj.value === 'string' &&
                  linkedInteractions
                ) {
                  const linked = linkedInteractions.get(actionObj.value);
                  if (linked) {
                    return {
                      ...actionObj,
                      value: linked,
                    } as InteractionAction;
                  }
                }
                return actionObj as InteractionAction;
              });

            return {
              ...(a as any),
              _id: aId,
              id: aId,
              questionId: qId,
              children: aChildren,
            };
          });

        return {
          ...(q as any),
          _id: qId,
          id: qId,
          children: qChildren,
        };
      });
  }

  private _computeLayout(options: {
    tree: QuestionTreeNode[];
    nodeHeights: Map<string, number>;
    linkedInteractions: Map<string, Interaction>;
    mainInteractionLabel?: string;
    mainInteractionVersion?: number;
    mainInteractionStatus?: InteractionStatus;
    mainInteractionKind?: 'interaction' | 'template';
  }): FlowLayout {
    const {
      tree,
      nodeHeights,
      linkedInteractions,
      mainInteractionLabel,
      mainInteractionVersion,
      mainInteractionStatus,
      mainInteractionKind,
    } = options;
    const {
      COLUMN_0_X,
      COLUMN_1_X,
      NODE_WIDTH,
      NODE_HEIGHT_ESTIMATE,
      NODE_GAP_Y,
      GROUP_GAP_Y,
      SUBFLOW_GAP_X,
    } = InteractionFlowComponent;
    const SUBFLOW_COL_SPAN = COLUMN_1_X - COLUMN_0_X;
    const h = (id: string) => nodeHeights.get(id) ?? NODE_HEIGHT_ESTIMATE;

    const questionNodes: FlowLayout['questionNodes'] = [];
    const answerNodes: FlowLayout['answerNodes'] = [];
    const subflowHeaders: FlowSubflowHeader[] = [];
    const edges: FlowEdge[] = [];

    // Header for the main interaction
    subflowHeaders.push({
      id: 'main-interaction-header',
      x: COLUMN_0_X,
      y: 20,
      width: SUBFLOW_COL_SPAN + NODE_WIDTH,
      label: mainInteractionLabel ?? '',
      version: mainInteractionVersion,
      status: mainInteractionStatus,
      kind: mainInteractionKind,
    });

    // Collect goto-interaction references to build edges after sub-flows are laid out
    const gotoInteractionRefs: {
      actionId: string;
      fromAnswerId: string;
      interactionId: string;
    }[] = [];

    let currentY = 80; // top padding — increased to give the header row more room

    for (const question of tree) {
      const answerGroupStartY = currentY;

      for (const answer of question.children) {
        const answerHeight = h(answer._id);
        answerNodes.push({
          id: answer._id,
          x: COLUMN_1_X,
          y: currentY,
          width: NODE_WIDTH,
          height: answerHeight,
          data: answer,
        });

        // Solid edge: question → answer
        edges.push({
          id: `q-to-a_${question._id}_${answer._id}`,
          type: 'q-to-a',
          fromId: question._id,
          toId: answer._id,
          path: '',
        });

        // Dashed edges from actions that reference other nodes
        for (const action of answer.children) {
          if (action.type === 'goto-question') {
            edges.push({
              id: `goto-question_${action._id}`,
              type: 'goto-question',
              fromId: answer._id,
              toId: action.value, // value is the target questionId (string)
              path: '',
            });
          } else if (action.type === 'goto-interaction') {
            const act = action as any;
            const targetId =
              typeof act.value === 'string'
                ? act.value
                : act.value?._id || act.value?.id;
            if (targetId) {
              gotoInteractionRefs.push({
                actionId: action._id,
                fromAnswerId: answer._id,
                interactionId: targetId,
              });
            }
          }
        }

        currentY += answerHeight + NODE_GAP_Y;
      }

      // Center the question node vertically over its answer group
      const questionHeight = h(question._id);
      let questionY: number;

      if (question.children.length > 0) {
        // answerGroupEndY = bottom edge of the last answer node
        const answerGroupEndY = currentY - NODE_GAP_Y;
        questionY = (answerGroupStartY + answerGroupEndY) / 2 - questionHeight / 2;
        // Replace the trailing NODE_GAP_Y with a larger GROUP_GAP_Y
        currentY = currentY - NODE_GAP_Y + GROUP_GAP_Y;
      } else {
        // No answers: place the question node at currentY and advance
        questionY = currentY;
        currentY += questionHeight + GROUP_GAP_Y;
      }

      questionNodes.push({
        id: question._id,
        x: COLUMN_0_X,
        y: questionY,
        width: NODE_WIDTH,
        height: questionHeight,
        data: question,
      });
    }

    // --- Sub-flows: render each unique linked interaction ---
    // BFS through linkedInteractions to collect all IDs in dependency order,
    // so deeply nested sub-flows (e.g. #2 → #3) are also rendered as columns.
    const uniqueInteractionIds: string[] = [];
    const visitedInteractionIds = new Set<string>();
    const bfsQueue = [...new Set(gotoInteractionRefs.map((r) => r.interactionId))];

    while (bfsQueue.length > 0) {
      const id = bfsQueue.shift()!;
      if (visitedInteractionIds.has(id)) continue;
      visitedInteractionIds.add(id);
      uniqueInteractionIds.push(id);

      const linkedInteraction = linkedInteractions.get(id);
      if (linkedInteraction) {
        for (const action of linkedInteraction.actions) {
          if (action.type === 'goto-interaction') {
            const act = action as any;
            const targetId =
              typeof act.value === 'string'
                ? act.value
                : act.value?._id || act.value?.id;
            if (targetId && !visitedInteractionIds.has(targetId)) {
              bfsQueue.push(targetId);
            }
          }
        }
      }
    }

    // Maps interaction ID → first question ID (for edge resolution)
    const interactionFirstQuestionIdMap = new Map<string, string>();

    uniqueInteractionIds.forEach((interactionId, subflowIndex) => {
      const interaction = linkedInteractions.get(interactionId);
      if (!interaction) return;

      const subflowBaseX =
        COLUMN_1_X +
        NODE_WIDTH +
        SUBFLOW_GAP_X +
        subflowIndex * (NODE_WIDTH + SUBFLOW_COL_SPAN + SUBFLOW_GAP_X);
      const subflowQX = subflowBaseX;
      const subflowAX = subflowBaseX + SUBFLOW_COL_SPAN;

      subflowHeaders.push({
        id: `subflow-header-${interactionId}`,
        x: subflowQX,
        y: 20,
        width: SUBFLOW_COL_SPAN + NODE_WIDTH,
        label: interaction.label,
        version: interaction.version,
        status: interaction.status,
        kind: interaction.clientId === null ? 'template' : 'interaction',
      });

      const subTree = this._buildTree(
        interaction.questions,
        interaction.answers,
        interaction.actions,
        linkedInteractions
      );
      let subCurrentY = 80;

      for (const question of subTree) {
        const subAnswerGroupStartY = subCurrentY;

        for (const answer of question.children) {
          const answerHeight = h(answer._id);
          answerNodes.push({
            id: answer._id,
            x: subflowAX,
            y: subCurrentY,
            width: NODE_WIDTH,
            height: answerHeight,
            data: answer,
          });

          edges.push({
            id: `q-to-a_${question._id}_${answer._id}`,
            type: 'q-to-a',
            fromId: question._id,
            toId: answer._id,
            path: '',
          });

          for (const action of answer.children) {
            if (action.type === 'goto-question') {
              edges.push({
                id: `goto-question_${action._id}`,
                type: 'goto-question',
                fromId: answer._id,
                toId: action.value,
                path: '',
              });
            } else if (action.type === 'goto-interaction') {
              const act = action as any;
              const subTargetId =
                typeof act.value === 'string'
                  ? act.value
                  : act.value?._id || act.value?.id;
              if (subTargetId) {
                gotoInteractionRefs.push({
                  actionId: action._id,
                  fromAnswerId: answer._id,
                  interactionId: subTargetId,
                });
              }
            }
          }

          subCurrentY += answerHeight + NODE_GAP_Y;
        }

        const questionHeight = h(question._id);
        let questionY: number;

        if (question.children.length > 0) {
          const subAnswerGroupEndY = subCurrentY - NODE_GAP_Y;
          questionY =
            (subAnswerGroupStartY + subAnswerGroupEndY) / 2 - questionHeight / 2;
          subCurrentY = subCurrentY - NODE_GAP_Y + GROUP_GAP_Y;
        } else {
          questionY = subCurrentY;
          subCurrentY += questionHeight + GROUP_GAP_Y;
        }

        questionNodes.push({
          id: question._id,
          x: subflowQX,
          y: questionY,
          width: NODE_WIDTH,
          height: questionHeight,
          data: question,
        });
      }

      if (subTree.length > 0) {
        const firstQId = subTree[0]._id;
        interactionFirstQuestionIdMap.set(interactionId, firstQId);
        const interId = String(
          (interaction as any)._id || (interaction as any).id || ''
        );
        if (interId) interactionFirstQuestionIdMap.set(interId, firstQId);
        const corrId = String(
          (interaction as any).correlationId ||
            (interaction as any).correlation_id ||
            ''
        );
        if (corrId) interactionFirstQuestionIdMap.set(corrId, firstQId);
      }
    });

    // Build goto-interaction edges now that sub-flow first question IDs are known
    for (const ref of gotoInteractionRefs) {
      const firstQId = interactionFirstQuestionIdMap.get(ref.interactionId);
      edges.push({
        id: `goto-interaction_${ref.actionId}`,
        type: 'goto-interaction',
        fromId: ref.fromAnswerId,
        toId: firstQId ?? `__missing_${ref.interactionId}`,
        path: '',
      });
    }

    // Canvas dimensions — derived from actual node extents
    const allXEnds = [
      ...questionNodes.map((n) => n.x + n.width),
      ...answerNodes.map((n) => n.x + n.width),
    ];
    const maxX = allXEnds.length > 0 ? Math.max(...allXEnds) : COLUMN_1_X + NODE_WIDTH;
    const canvasWidth = maxX + 100;
    const canvasHeight = Math.max(currentY + 60, 400);

    return {
      questionNodes,
      answerNodes,
      subflowHeaders,
      edges,
      canvasWidth,
      canvasHeight,
    };
  }
}
